import {
  Avatar,
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Form,
  Input,
  message,
  Row,
  Space,
  Spin,
  Statistic,
  Typography,
} from 'antd';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import '../styles/quill-custom.css';
import {
  BankOutlined,
  CalendarOutlined,
  SaveOutlined,
  TeamOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import ImageUpload from '../components/ImageUpload';
import api from '../config/api';
import { textLocaleTag } from '../i18n/locale';
import { useAuthStore } from '../store/authStore';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface Organization {
  id: string;
  name: string;
  slug: string;
  description?: string;
  logo?: string;
  aboutOrganizer?: string;
  accountHolder?: string;
  accountHolderAddress?: string;
  accountHolderRegistryNumber?: string;
  accountHolderTaxId?: string;
  accountNumber?: string;
  bankName?: string;
  paymentCode?: string;
  paymentPurpose?: string;
  currency?: string;
  createdAt: string;
  updatedAt: string;
  _count?: {
    users: number;
    events: number;
  };
}

const MyOrganization: React.FC = () => {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const { user } = useAuthStore();
  const { t, i18n } = useTranslation();

  const quillModules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['blockquote', 'code-block'],
        ['link', 'image'],
        ['clean'],
      ],
    }),
    []
  );

  const quillFormats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'blockquote',
    'code-block',
    'link',
    'image',
  ];

  useEffect(() => {
    fetchOrganization();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchOrganization = async () => {
    if (!user?.organizationId) {
      message.error(t('myOrganization.noOrganization'));
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await api.get(`/organizations/${user.organizationId}`);
      setOrganization(response.data);
      form.setFieldsValue({
        name: response.data.name,
        slug: response.data.slug,
        description: response.data.description,
        logo: response.data.logo || '',
        aboutOrganizer: response.data.aboutOrganizer || '',
        accountHolder: response.data.accountHolder || '',
        accountHolderAddress: response.data.accountHolderAddress || '',
        accountHolderRegistryNumber: response.data.accountHolderRegistryNumber || '',
        accountHolderTaxId: response.data.accountHolderTaxId || '',
        accountNumber: response.data.accountNumber || '',
        bankName: response.data.bankName || '',
        paymentCode: response.data.paymentCode || '',
        paymentPurpose: response.data.paymentPurpose || '',
        currency: response.data.currency || '',
      });
    } catch (error) {
      message.error(t('myOrganization.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    if (!organization) return;

    setSaving(true);
    try {
      const response = await api.patch(`/organizations/${organization.id}`, {
        name: values.name,
        slug: values.slug,
        description: values.description,
        logo: values.logo || '',
        aboutOrganizer: values.aboutOrganizer,
        accountHolder: values.accountHolder || undefined,
        accountHolderAddress: values.accountHolderAddress || undefined,
        accountHolderRegistryNumber: values.accountHolderRegistryNumber || undefined,
        accountHolderTaxId: values.accountHolderTaxId || undefined,
        accountNumber: values.accountNumber || undefined,
        bankName: values.bankName || undefined,
        paymentCode: values.paymentCode || undefined,
        paymentPurpose: values.paymentPurpose || undefined,
        currency: values.currency || undefined,
      });
      setOrganization(response.data);
      message.success(t('myOrganization.updated'));
    } catch (error: any) {
      message.error(error.response?.data?.message || t('messages.save_error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!organization) {
    return (
      <Card>
        <Text type="secondary">{t('myOrganization.noOrganization')}</Text>
      </Card>
    );
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space>
            <Avatar
              size={48}
              icon={<BankOutlined />}
              src={organization.logo}
              style={{ backgroundColor: '#1890ff' }}
            />
            <div>
              <Title level={2} style={{ margin: 0 }}>
                {t('myOrganization.title')}
              </Title>
              <Text type="secondary">{organization.name}</Text>
            </div>
          </Space>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title={t('myOrganization.statUsers')}
              value={organization._count?.users || 0}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title={t('myOrganization.statEvents')}
              value={organization._count?.events || 0}
              prefix={<CalendarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title={t('myOrganization.statCreated')}
              value={new Date(organization.createdAt).toLocaleDateString(
                textLocaleTag(i18n.language)
              )}
              prefix={<TrophyOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card title={t('myOrganization.infoTitle')}>
        <Descriptions bordered column={{ xs: 1, sm: 2 }} style={{ marginBottom: 24 }}>
          <Descriptions.Item label={t('myOrganization.fieldId')}>
            <Text code>{organization.id}</Text>
          </Descriptions.Item>
          <Descriptions.Item label={t('myOrganization.fieldSlug')}>
            <Text code>{organization.slug}</Text>
          </Descriptions.Item>
          <Descriptions.Item label={t('myOrganization.fieldCreatedAt')}>
            {new Date(organization.createdAt).toLocaleString(textLocaleTag(i18n.language))}
          </Descriptions.Item>
          <Descriptions.Item label={t('myOrganization.fieldUpdatedAt')}>
            {new Date(organization.updatedAt).toLocaleString(textLocaleTag(i18n.language))}
          </Descriptions.Item>
        </Descriptions>

        <Divider orientation="left">{t('myOrganization.settingsDivider')}</Divider>

        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label={t('organizationForm.nameLabel')}
                rules={[{ required: true, message: t('common.requiredField') }]}
              >
                <Input
                  prefix={<BankOutlined />}
                  placeholder={t('organizationForm.namePlaceholder')}
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="slug"
                label={t('organizationForm.slugLabel')}
                rules={[
                  { required: true, message: t('common.requiredField') },
                  {
                    pattern: /^[a-z0-9-]+$/,
                    message: t('organizationForm.slugPattern'),
                  },
                ]}
              >
                <Input
                  placeholder={t('organizationForm.slugPlaceholder')}
                  size="large"
                  disabled={user?.role !== 'SUPER_ADMIN'}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label={t('common.description')}>
            <TextArea
              rows={4}
              placeholder={t('organizationForm.descriptionPlaceholder')}
              showCount
              maxLength={500}
            />
          </Form.Item>

          <Form.Item name="logo" label={t('myOrganization.logoLabel')}>
            <ImageUpload
              uploadUrl="/api/upload/logo"
              fieldName="logo"
              placeholder={t('myOrganization.logoPlaceholder')}
              maxSize={2}
            />
          </Form.Item>

          <Form.Item name="aboutOrganizer" label={t('myOrganization.aboutLabel')}>
            <ReactQuill
              theme="snow"
              modules={quillModules}
              formats={quillFormats}
              placeholder={t('myOrganization.aboutPlaceholder')}
              style={{ minHeight: '200px' }}
            />
          </Form.Item>

          <Divider orientation="left">{t('organizationForm.bankDivider')}</Divider>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            {t('organizationForm.bankHint')}
          </Text>

          <Form.Item
            name="accountHolder"
            label={t('organizationForm.accountHolderLabel')}
            help={t('organizationForm.accountHolderHelp')}
            rules={[{ max: 70, message: t('organizationForm.accountHolderMax') }]}
          >
            <Input
              maxLength={70}
              showCount
              placeholder={t('organizationForm.accountHolderPlaceholder')}
            />
          </Form.Item>

          <Form.Item
            name="accountHolderAddress"
            label={t('organizationForm.addressLabel')}
            help={t('organizationForm.addressHelp')}
            rules={[{ max: 200, message: t('organizationForm.addressMax') }]}
          >
            <TextArea
              rows={2}
              maxLength={200}
              showCount
              placeholder={t('organizationForm.addressPlaceholder')}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="accountHolderRegistryNumber"
                label={t('organizationForm.registryNumberLabel')}
                rules={[{ max: 50, message: t('organizationForm.registryNumberMax') }]}
              >
                <Input
                  maxLength={50}
                  placeholder={t('organizationForm.registryNumberPlaceholder')}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="accountHolderTaxId"
                label={t('organizationForm.taxIdLabel')}
                rules={[{ max: 50, message: t('organizationForm.taxIdMax') }]}
              >
                <Input maxLength={50} placeholder={t('organizationForm.taxIdPlaceholder')} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={16}>
              <Form.Item
                name="accountNumber"
                label={t('organizationForm.accountNumberLabel')}
                help={t('organizationForm.accountNumberHelp')}
                rules={[
                  {
                    pattern: /^[\d\s-]{18,22}$/,
                    message: t('organizationForm.accountNumberPattern'),
                  },
                ]}
              >
                <Input maxLength={22} placeholder="000-0000000000000-00" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="currency"
                label={t('organizationForm.currencyLabel')}
                help={t('organizationForm.currencyHelp')}
                rules={[
                  {
                    pattern: /^[A-Z]{3}$/,
                    message: t('organizationForm.currencyPattern'),
                  },
                ]}
                normalize={(v: string | undefined) => v?.toUpperCase()}
              >
                <Input maxLength={3} placeholder={t('organizationForm.currencyPlaceholder')} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="bankName"
                label={t('organizationForm.bankNameLabel')}
                rules={[{ max: 70, message: t('organizationForm.bankNameMax') }]}
              >
                <Input maxLength={70} placeholder={t('organizationForm.bankNamePlaceholder')} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="paymentCode"
                label={t('organizationForm.paymentCodeLabel')}
                help={t('organizationForm.paymentCodeHelp')}
                rules={[
                  {
                    pattern: /^\d{3}$/,
                    message: t('organizationForm.paymentCodePattern'),
                  },
                ]}
              >
                <Input maxLength={3} placeholder={t('organizationForm.paymentCodePlaceholder')} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="paymentPurpose"
            label={t('organizationForm.paymentPurposeLabel')}
            help={t('organizationForm.paymentPurposeHelp')}
            rules={[
              {
                max: 35,
                message: t('organizationForm.paymentPurposeMax'),
              },
            ]}
          >
            <TextArea
              rows={2}
              maxLength={35}
              showCount
              placeholder={t('organizationForm.paymentPurposePlaceholder')}
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={saving}
              icon={<SaveOutlined />}
              size="large"
            >
              {t('myOrganization.saveChanges')}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default MyOrganization;
